import React, { useState } from 'react';
import {
  Grid,
  Fab,
  FormControlLabel,
  Switch,
  Tooltip,
  styled,
  IconButton,
  TextField,
  Box,
  Button,
  Divider,
  Paper
} from '@mui/material';
import {
  Mic,
  MicOff,
  Settings,
  Pause,
  Call,
  CallEnd,
  Transform,
  PlayArrow,
  PhoneForwarded,
  Cancel,
  SwapCalls,
  CallMerge,
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  Send as TransferIcon,
  VideoCall as VideoCallIcon,
  RecordVoiceOver as RecordIcon,
  PresentToAll as ScreenShareIcon,
  Add as AddParticipantIcon
} from '@mui/icons-material';

import SearchList from './search-list';

const Root = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1)
}));

const FabStyled = styled(Fab)(({ theme }) => ({
  width: '44px',
  height: '44px',
  margin: theme.spacing(0.5),
  background: 'transparent',
  color: theme.palette.text.secondary,
  boxShadow: 'none',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    boxShadow: 'none',
  },
  '&:active': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: 'transparent',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
    boxShadow: 'none'
  }
}));

const ActionFab = styled(Fab)(({ theme }) => ({
  width: '44px',
  height: '44px',
  margin: theme.spacing(0.5),
  boxShadow: 'none',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    boxShadow: 'none',
  },
  '&.call': {
    background: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : '#e6f4ea',
    color: '#10b981',
    '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.25)' : '#cce8d5' }
  },
  '&.end-call': {
    background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#fce8e8',
    color: '#ef4444',
    '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.25)' : '#fad1d1' }
  },
  '&.transfer': {
    background: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
    color: '#f59e0b',
    '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.25)' : '#fde68a' }
  },
  '&.hold': {
    background: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
    color: '#3b82f6',
    '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe' }
  }
}));

const CallButton = styled(Fab)(({ theme }) => ({
  color: 'white',
  background: '#3acd7e',
  width: '40px',
  height: '40px',
  margin: theme.spacing(0.5),
  '&:hover': {
    background: '#16b364' // chateauGreen[500]
  }
}));

const EndCallButton = styled(Fab)(({ theme }) => ({
  color: 'white',
  background: theme => theme.palette.error.main,
  width: '40px',
  height: '40px',
  margin: theme.spacing(0.5),
  '&:hover': {
    background: theme => theme.palette.error.dark
  }
}));

const GridRaw = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  marginBottom: theme.spacing(2),
  width: '100%'
}));

const GridLastRaw = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(2)
}));

const KeypadContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2, 1)
}));

const DialPadButton = styled(Button)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? '#1f2937' : '#f3f4f6',
  color: theme.palette.text.primary,
  border: 'none',
  boxShadow: 'none',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb',
  },
  '&:active': {
    backgroundColor: theme.palette.mode === 'dark' ? '#4b5563' : '#d1d5db',
  },
  '& .num': {
    fontSize: '24px',
    fontWeight: '400',
    lineHeight: '1',
    fontFamily: 'Inter, Roboto, sans-serif',
  },
  '& .alpha': {
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '1px',
    color: theme.palette.text.secondary,
    marginTop: '2px',
    height: '10px',
    textTransform: 'none',
  }
}));

const QuickActionsBar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  '& .MuiSvgIcon-root': {
    fontSize: '1rem'
  }
}));

function KeypadBlock({
  handleCallAttendedTransfer,
  handleCallTransfer,
  handlePressKey,
  handleMicMute,
  handleCall,
  handleEndCall,
  activeChanel,
  keyVariant = 'default',
  handleHold,
  asteriskAccounts = [],
  dialState,
  setDialState
}) {
  const {
    inCall,
    muted,
    hold,
    sessionId,
    inAnswer,
    inAnswerTransfer,
    inConference,
    inTransfer,
    transferControl,
    allowTransfer,
    allowAttendedTransfer
  } = activeChanel;
  const [anchorElTransfer, setAnchorElTransfer] = useState(null);
  const [anchorElAttended, setAnchorElAttended] = useState(null);
  const handleClickTransferCall = (event) => {
    if (dialState.match(/^[0-9]+$/) != null) {
      handleCallTransfer(dialState);
      setDialState('');
      return;
    }
    setAnchorElTransfer(event.currentTarget);
  };
  const TransferListClick = (id) => {
    if (id) {
      handleCallTransfer(id);
    }
  };
  const handleClickAttendedTransfer = (event) => {
    if (dialState.match(/^[0-9]+$/) != null) {
      handleCallAttendedTransfer('transfer', {});
      setDialState('');
      return;
    }
    setAnchorElAttended(event.currentTarget);
  };
  const AttendedTransferListClick = (id) => {
    if (id) {
      handleCallAttendedTransfer('transfer', id);
      setDialState('');
    }
  };
  
  const keys = [
    { num: '1', alpha: '' },
    { num: '2', alpha: 'ABC' },
    { num: '3', alpha: 'DEF' },
    { num: '4', alpha: 'GHI' },
    { num: '5', alpha: 'JKL' },
    { num: '6', alpha: 'MNO' },
    { num: '7', alpha: 'PQRS' },
    { num: '8', alpha: 'TUV' },
    { num: '9', alpha: 'WXYZ' },
    { num: '*', alpha: '' },
    { num: '0', alpha: '+' },
    { num: '#', alpha: '' }
  ];

  const onKeyPress = (key) => {
    if (inCall && sessionId) {
      handlePressKey(key, true)
    } else {
      handlePressKey(key, false)
    }
  }

  return (
    <Root>
        <KeypadContainer>
          <GridRaw container spacing={0}>
            <Grid item xs={12}>
              <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} justifyContent="center" mx="auto" width="max-content">
                {keys.map((key) => (
                  <DialPadButton
                    key={key.num}
                    onClick={() => onKeyPress(key.num)}
                    disableRipple
                  >
                    <span className="num">{key.num}</span>
                    <span className="alpha">{key.alpha}</span>
                  </DialPadButton>
                ))}
              </Box>
            </Grid>
          </GridRaw>
          <GridRaw container spacing={0}>
            <Grid item xs={3}>
              <Grid item xs={12}>
                <Tooltip title={muted ? 'Unmute Microphone' : 'Mute Microphone'} disableFocusListener disableTouchListener>
                  <div>
                    <FabStyled
                      disabled={!inCall}
                      value={inCall}
                      size="small"
                      aria-label={muted ? 'unmute' : 'mute'}
                      onClick={handleMicMute}
                      sx={theme => ({
                        // Adaptive colors based on theme mode
                        bgcolor: muted 
                          ? (theme.palette.mode === 'dark' ? '#ff5252' : '#f44336') 
                          : theme.palette.primary.main,
                        color: '#ffffff',
                        boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                        '&:hover': {
                          bgcolor: muted 
                            ? (theme.palette.mode === 'dark' ? '#ff1744' : '#d32f2f') 
                            : theme.palette.primary.dark,
                        },
                        '&.Mui-disabled': {
                          bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.12)' 
                            : 'rgba(0,0,0,0.12)',
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.3)' 
                            : 'rgba(0,0,0,0.26)'
                        }
                      })}
                    >
                      {muted ? <MicOff /> : <Mic />}
                    </FabStyled>
                  </div>
                </Tooltip>
              </Grid>
              <Grid hidden>
                <FormControlLabel
                  control={<Switch size="small" checked onChange={() => {}} />}
                  label="Mute"
                />
              </Grid>
            </Grid>
            <Grid item xs={3}>
              <Tooltip title={hold ? 'Resume Call' : 'Hold Call'} disableFocusListener disableTouchListener>
                <div>
                  <FabStyled
                    disabled={!inCall || !inAnswer}
                    size="small"
                    aria-label={hold ? 'resume' : 'hold'}
                    onClick={() => {
                      handleHold(sessionId, hold);
                    }}
                    sx={theme => ({
                      // Adaptive colors based on theme mode
                      bgcolor: hold 
                        ? '#3acd7e' 
                        : theme.palette.primary.main,
                      color: '#ffffff',
                      boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                      border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                      '&:hover': {
                        bgcolor: hold 
                          ? '#16b364' 
                          : theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.12)' 
                          : 'rgba(0,0,0,0.12)',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.3)' 
                          : 'rgba(0,0,0,0.26)'
                      }
                    })}
                  >
                    {hold ? <PlayArrow /> : <Pause />}
                  </FabStyled>
                </div>
              </Tooltip>
            </Grid>
            <Grid item xs={3}>
              <Tooltip title="Transfer Call">
                <div>
                  <FabStyled
                    disabled={!inCall || !inAnswer || hold || !allowAttendedTransfer}
                    size="small"
                    aria-label="transfer-call"
                    onClick={handleClickTransferCall}
                    aria-describedby="transferredBox"
                    sx={theme => ({
                      bgcolor: theme.palette.warning.main,
                      color: '#ffffff',
                      boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                      border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                      '&:hover': {
                        bgcolor: theme.palette.warning.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.12)' 
                          : 'rgba(0,0,0,0.12)',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.3)' 
                          : 'rgba(0,0,0,0.26)'
                      }
                    })}
                  >
                    <PhoneForwarded />
                  </FabStyled>
                </div>
              </Tooltip>
              <SearchList
                asteriskAccounts={asteriskAccounts}
                onClickList={(id) => TransferListClick(id)}
                ariaDescribedby="transferredBox"
                anchorEl={anchorElTransfer}
                setAnchorEl={setAnchorElTransfer}
              />
            </Grid>
            <Grid item xs={3}>
              <Tooltip title="Attended Transfer">
                <div>
                  <FabStyled
                    disabled={!inCall || !inAnswer || hold || !allowTransfer}
                    size="small"
                    aria-label="attended-transfer"
                    onClick={handleClickAttendedTransfer}
                    aria-describedby="attendedBox"
                    sx={theme => ({
                      bgcolor: theme.palette.mode === 'dark' ? '#9c27b0' : '#8e24aa',
                      color: '#ffffff',
                      boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                      border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? '#7b1fa2' : '#6a1b9a',
                      },
                      '&.Mui-disabled': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.12)' 
                          : 'rgba(0,0,0,0.12)',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.3)' 
                          : 'rgba(0,0,0,0.26)'
                      }
                    })}
                  >
                    <Transform />
                  </FabStyled>
                </div>
              </Tooltip>
              <SearchList
                asteriskAccounts={asteriskAccounts}
                onClickList={AttendedTransferListClick}
                ariaDescribedby="attendedBox"
                anchorEl={anchorElAttended}
                setAnchorEl={setAnchorElAttended}
              />
            </Grid>
            {inAnswerTransfer
            && !inConference
            && inTransfer
            && transferControl ? (
              <Grid container spacing={0}>
                <GridRaw item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={3}>
                      <Tooltip title="Conference" aria-label="conference">
                        <span>
                          <ActionFab
                            disabled={false}
                            size="small"
                            aria-label="conference"
                            onClick={() => {
                              handleCallAttendedTransfer('merge', {});
                            }}
                            sx={theme => ({
                              bgcolor: theme.palette.mode === 'dark' ? '#81c784' : '#4caf50',
                              color: '#ffffff',
                              boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                              border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? '#66bb6a' : '#388e3c',
                              }
                            })}
                          >
                            <CallMerge />
                          </ActionFab>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Swap Caller" aria-label="swap-caller">
                        <span>
                          <ActionFab
                            disabled={false}
                            size="small"
                            aria-label="swap-caller"
                            onClick={() => {
                              handleCallAttendedTransfer('swap', {});
                            }}
                            sx={theme => ({
                              bgcolor: theme.palette.mode === 'dark' ? '#64b5f6' : '#2196f3',
                              color: '#ffffff',
                              boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                              border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? '#42a5f5' : '#1976d2',
                              }
                            })}
                          >
                            <SwapCalls />
                          </ActionFab>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Pass Call" aria-label="pass-call">
                        <span>
                          <ActionFab
                            disabled={false}
                            size="small"
                            aria-label="pass-call"
                            onClick={() => {
                              handleCallAttendedTransfer('finish', {});
                            }}
                            sx={theme => ({
                              bgcolor: theme.palette.mode === 'dark' ? '#ffb74d' : '#ff9800',
                              color: '#ffffff',
                              boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                              border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? '#ffa726' : '#f57c00',
                              }
                            })}
                          >
                            <PhoneForwarded />
                          </ActionFab>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Cancel Transfer" aria-label="cancel-transfer">
                        <span>
                          <ActionFab
                            disabled={false}
                            size="small"
                            aria-label="cancel-transfer"
                            onClick={() => {
                              handleCallAttendedTransfer('cancel', {});
                            }}
                            sx={theme => ({
                              bgcolor: theme.palette.error.main,
                              color: '#ffffff',
                              boxShadow: theme.palette.mode === 'dark' ? 5 : 3,
                              border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.15)' : 'none',
                              '&:hover': {
                                bgcolor: theme.palette.error.dark,
                              }
                            })}
                          >
                            <Cancel />
                          </ActionFab>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </GridRaw>
              </Grid>
              ) : (
                <div />
              )}
          </GridRaw>

          <GridLastRaw container spacing={0}>
            {inCall === false ? (
              <FabStyled
                size="large"
                aria-label="Make Call"
                onClick={handleCall}
                sx={theme => ({
                  width: '64px',
                  height: '64px',
                  bgcolor: '#10b981',
                  color: '#ffffff',
                  boxShadow: 'none',
                  border: 'none',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: '#059669',
                    boxShadow: 'none',
                  },
                  '& .MuiSvgIcon-root': { fontSize: '28px' }
                })}
              >
                <Call />
              </FabStyled>
            ) : (
              <FabStyled
                size="large"
                aria-label="End Call"
                onClick={handleEndCall}
                sx={theme => ({
                  width: '64px',
                  height: '64px',
                  bgcolor: '#ef4444',
                  color: '#ffffff',
                  boxShadow: 'none',
                  border: 'none',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: '#dc2626',
                    boxShadow: 'none',
                  },
                  '& .MuiSvgIcon-root': { fontSize: '28px' }
                })}
              >
                <CallEnd />
              </FabStyled>
            )}
          </GridLastRaw>


        </KeypadContainer>
    </Root>
  );
}
export default KeypadBlock;
